import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { communityService } from '../../services/api';
import ScheduleHeatmap from './ScheduleHeatmap';
import {
  DAY_LABELS_MEDIUM_ES,
  minutesToHHMM,
} from '../../utils/schedule';

/**
 * Resumen compacto de "dónde encontrar a {userName}" para mostrar en
 * vistas estrechas (ej. el álbum de otra persona). Una línea horizontal
 * con los primeros rangos; click → modal con el calendario semanal
 * completo (igual al de la página de la comunidad).
 *
 * Props:
 *   userId
 *   userName
 *   entries: lista de comunidades compartidas con `{communityId, communityName, sedes}`.
 *            Vienen del response del álbum / producto.
 */

const MAX_INLINE_RANGES = 3;

function ChipModal({ open, onClose, userName, userId, entries }) {
  const { t } = useTranslation();
  // Por defecto, abrimos la primera comunidad. Si hay varias, mostramos un
  // selector arriba.
  const [activeIdx, setActiveIdx] = useState(0);
  React.useEffect(() => { if (open) setActiveIdx(0); }, [open]);

  const active = entries?.[activeIdx];
  const { data: schedule, isLoading } = useQuery({
    queryKey: ['user-schedule', String(active?.communityId), String(userId)],
    queryFn: async () => (await communityService.getUserScheduleInCommunity(active.communityId, userId)).data,
    enabled: open && !!active && !!userId,
    staleTime: 60_000,
  });

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end tablet-sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-container-lowest rounded-t-2xl tablet-sm:rounded-2xl w-full tablet-sm:max-w-xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-outline-variant/15">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-on-surface truncate">
              {userName
                ? t('Dónde encontrar a {{name}}', { name: userName })
                : t('Horarios')}
            </h3>
            {entries && entries.length > 0 && (
              <p className="text-[11px] text-on-surface-variant mt-0.5 truncate">
                {entries.length === 1
                  ? entries[0].communityName
                  : t('{{n}} comunidades compartidas', { n: entries.length })}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-surface-container flex items-center justify-center text-on-surface-variant transition-colors shrink-0"
            aria-label={t('Cerrar')}
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Tabs de comunidad cuando hay > 1 */}
        {entries && entries.length > 1 && (
          <div className="px-3 py-2 border-b border-outline-variant/15 flex gap-1 overflow-x-auto hide-scrollbar">
            {entries.map((e, i) => (
              <button
                key={e.communityId}
                type="button"
                onClick={() => setActiveIdx(i)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                  i === activeIdx
                    ? 'bg-primary text-on-primary'
                    : 'text-on-surface-variant hover:bg-surface-container-high/50'
                }`}
              >
                {e.communityName}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="text-xs text-on-surface-variant py-4">{t('Cargando horarios...')}</div>
          ) : schedule && Array.isArray(schedule.days) ? (
            <ScheduleHeatmap days={schedule.days} sedes={active?.sedes || []} />
          ) : (
            <div className="text-xs text-on-surface-variant/60 italic py-4">{t('Sin horarios.')}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Línea compacta con los primeros N rangos de la primera comunidad.
 * Estructura: "📅 Lun 8–12 · Mar 14–18 · Sáb 10–13  (+2)  Ver"
 *
 * El componente fetchea solo el schedule de la PRIMERA comunidad para tener
 * algo que mostrar inline. Las demás comunidades aparecen en el modal.
 */
function OtherUserScheduleSummary({ userId, userName, entries }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const safeEntries = (entries || []).filter((e) => e && e.communityId);
  const first = safeEntries[0];

  // Solo fetcheamos la 1ra comunidad para el preview inline.
  const { data: firstSchedule } = useQuery({
    queryKey: ['user-schedule', String(first?.communityId), String(userId)],
    queryFn: async () => (await communityService.getUserScheduleInCommunity(first.communityId, userId)).data,
    enabled: !!first && !!userId,
    staleTime: 60_000,
  });

  if (safeEntries.length === 0) return null;

  // Aplanamos los rangos del schedule en orden de día → string.
  const inlineParts = [];
  let extraCount = 0;
  if (firstSchedule?.days) {
    let total = 0;
    for (let d = 0; d < 7; d++) {
      const day = firstSchedule.days[d] || [];
      for (const r of day) {
        total++;
        if (inlineParts.length < MAX_INLINE_RANGES) {
          inlineParts.push(
            `${DAY_LABELS_MEDIUM_ES[d]} ${minutesToHHMM(r.start)}–${minutesToHHMM(r.end)}`
          );
        }
      }
    }
    extraCount = Math.max(0, total - MAX_INLINE_RANGES);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-surface-container/60 hover:bg-surface-container-high/60 border border-outline-variant/20 text-left transition-colors group"
        title={t('Ver horarios completos')}
      >
        <span className="material-symbols-outlined text-[16px] text-primary shrink-0">schedule</span>
        <span className="flex-1 min-w-0">
          {inlineParts.length > 0 ? (
            <span className="text-[11px] text-on-surface truncate block">
              {inlineParts.join(' · ')}
              {extraCount > 0 && (
                <span className="text-on-surface-variant"> +{extraCount}</span>
              )}
            </span>
          ) : (
            <span className="text-[11px] text-on-surface-variant truncate block">
              {userName
                ? t('Ver horarios de {{name}}', { name: userName })
                : t('Ver horarios')}
            </span>
          )}
        </span>
        <span className="material-symbols-outlined text-[14px] text-on-surface-variant/60 group-hover:text-primary transition-colors shrink-0">
          open_in_full
        </span>
      </button>
      <ChipModal
        open={open}
        onClose={() => setOpen(false)}
        userName={userName}
        userId={userId}
        entries={safeEntries}
      />
    </>
  );
}

export default OtherUserScheduleSummary;
